<header class="box noshrink flex align-flex-start gap-1 px-6 pb-3">
  <div class="chat-thread__shrinkable box grow flex align-center gap-3">
    <button
      *ngIf="showBack"
      mat-icon-button
      type="button"
      class="box noshrink m-0 p-0"
      [attr.aria-label]="'bidChat.Back' | translate"
      (click)="back.emit()"
    >
      <app-icon name="arrow-left" size="xs" class="text-600"></app-icon>
    </button>

    <app-avatar
      [round]="true"
      [name]="header.counterpartName"
      bgColor="var(--color-secondry-500)"
      fgColor="var(--color-white)"
      [size]="44"
      class="box noshrink"
    ></app-avatar>

    <div class="chat-thread__shrinkable box grow flex column">
      <div class="flex align-center gap-1">
        <span class="h6 m-0 weight-normal text-900 text-ellipsis text-nowrap">{{ counterpartName }}</span>
        <app-icon *ngIf="header.isVerified" name="outline-shield-tick" direction="ltr" size="xs" class="box noshrink text-success"></app-icon>
      </div>
      <div *ngIf="header.originalBid" class="flex align-center gap-1 small text-nowrap">
        <span class="weight-medium text-600">{{ 'bidChat.OriginalBid' | translate }}</span>
        <span riyalCurrency class="text-secondry">{{ header.originalBid | number: '1.2-2' }}</span>
      </div>
    </div>
  </div>

  <button mat-icon-button type="button" class="box noshrink" [attr.aria-label]="'bidChat.Close' | translate" (click)="closed.emit()">
    <app-icon name="outline-cross" size="xxs" class="text-600"></app-icon>
  </button>
</header>

<div
  #messagesScroll
  class="chat-thread__shrinkable box grow scrollable--y flex column gap-3 px-6 py-3 brd-t brd-200"
>
  <!-- Loading: placeholder bubbles, like a messaging app, instead of a spinner. -->
  <ng-container *ngIf="isLoading">
    <div *ngFor="let own of [false, true, false, true]" class="flex column gap-1" [ngClass]="own ? 'align-flex-end' : 'align-flex-start'">
      <span class="chat-thread__skeleton chat-thread__skeleton--meta radius-1 bg-100"></span>
      <span class="chat-thread__skeleton radius-3" [ngClass]="own ? 'bg-primary-lighter w-58' : 'bg-100 w-75'"></span>
    </div>
  </ng-container>

  <ng-container *ngIf="!isLoading" [ngSwitch]="state?.phase">
    <div *ngSwitchCase="'error'" class="box grow flex column align-center justify-center gap-3 text-center p-6">
      <app-icon name="outline-danger" direction="ltr" size="md" class="text-warn"></app-icon>
      <p class="small text-600 m-0">{{ 'bidChat.TryAgainLater' | translate }}</p>
      <button mat-stroked-button type="button" (click)="retry()">{{ 'bidChat.Retry' | translate }}</button>
    </div>

    <ng-container *ngSwitchCase="'ready'">
      <div *ngIf="state.messagesLoaded && !feed.length" class="box grow flex column align-center justify-center gap-3 text-center px-8">
        <span class="flex align-center justify-center p-3 radius-2 bg-primary-lighter">
          <app-icon name="twotone-message-text" size="lg" class="chat-thread__empty-icon text-primary"></app-icon>
        </span>
        <span class="flex column align-center gap-1">
          <span class="h5 m-0 weight-normal text-900">
            {{ (state.mode === 'history' ? 'bidChat.NoMessagesToShow' : 'bidChat.StartConversation') | translate }}
          </span>
          <span class="small text-600">
            {{ (state.mode === 'history' ? 'bidChat.NoChatHistoryWith' : 'bidChat.SendMessageTo') | translate: { name: counterpartName } }}
          </span>
        </span>
      </div>

      <ng-container *ngFor="let item of feed; trackBy: trackByFeedKey">
        <div *ngIf="item.kind === 'day'" class="chat-thread__meta text-500 text-center">{{ item.label }}</div>

        <!-- OFFER message: a card, per design (title + time, price, then waiting / Accept). Its look follows offerStatus. -->
        <div
          *ngIf="item.kind === 'message' && item.message.type === messageType.Offer"
          class="flex column gap-2 p-4 radius-3 brd brd-200"
          [ngClass]="isClosedOffer(item) ? 'bg-50' : 'bg-white'"
        >
          <div class="flex align-center justify-space-between gap-2">
            <span class="h6 m-0 weight-normal flex align-center gap-1" [ngClass]="offerTitleClass(item)">
              <ng-container *ngIf="!isFailedOffer(item)" [ngSwitch]="item.message.offerStatus">
                <app-icon *ngSwitchCase="offerStatus.Accepted" name="outline-tick-circle" direction="ltr" size="xs"></app-icon>
                <ng-container *ngSwitchCase="offerStatus.Active">
                  <app-icon *ngIf="!item.isOwn" name="outline-notification" direction="ltr" size="xs"></app-icon>
                </ng-container>
                <!-- Rejected, deactivated and any unknown status. -->
                <app-icon *ngSwitchDefault name="outline-forbidden" direction="ltr" size="xs"></app-icon>
              </ng-container>
              {{ offerTitleKey(item) | translate }}
            </span>
            <span class="chat-thread__meta text-600 text-nowrap flex align-center gap-1">
              {{ item.message.sentAt | date: 'h:mm a' }}
              <ng-container *ngIf="item.isMine">
                <ng-container *ngTemplateOutlet="deliveryIcon; context: { $implicit: item.message }"></ng-container>
              </ng-container>
            </span>
          </div>

          <div class="flex column gap-1">
            <!-- Per design, only the customer's own offer carries a label; a received one shows the price alone. -->
            <span *ngIf="item.isOwn" class="small" [ngClass]="isClosedOffer(item) ? 'text-300' : 'text-primary weight-medium'">
              {{ item.isMine ? ('bidChat.YouCounterOffered' | translate) : item.message.senderName }}
            </span>
            <span riyalCurrency class="h5 m-0 weight-semibold" [ngClass]="isClosedOffer(item) ? 'text-300' : 'text-900'">
              {{ item.message.offerPrice | number: '1.0-2' }}
            </span>
          </div>

          <span *ngIf="item.isOwn && isActiveOffer(item)" class="small text-600">
            {{ 'bidChat.WaitingForAcceptance' | translate: { name: counterpartName } }}
          </span>

          <button
            *ngIf="canAccept(item)"
            mat-flat-button
            type="button"
            color="accent"
            class="w-100 radius-2 text-white"
            [disabled]="state.acceptStatus === 'accepting' || state.acceptStatus === 'accepted'"
            (click)="acceptOffer(item.message)"
          >
            {{ 'bidChat.AcceptOffer' | translate }}
          </button>
          <p *ngIf="canAccept(item) && state.acceptStatus === 'failed' && state.acceptOfferId === item.message.id" class="small text-warn m-0" role="alert">{{ 'bidChat.AcceptFailed' | translate }}</p>
          <ng-container *ngIf="item.isMine && item.message.delivery === 'failed'">
            <ng-container *ngTemplateOutlet="notSent; context: { $implicit: item.message }"></ng-container>
          </ng-container>
        </div>

        <div
          *ngIf="item.kind === 'message' && item.message.type !== messageType.Offer"
          class="flex column gap-1"
          [ngClass]="item.isOwn ? 'align-flex-end' : 'align-flex-start'"
        >
          <div class="chat-thread__meta weight-medium flex align-center gap-2 text-nowrap">
            <span class="text-primary-300">{{ item.isMine ? ('bidChat.You' | translate) : item.message.senderName || counterpartName }}</span>
            <span class="text-600">{{ item.message.sentAt | date: 'h:mm a' }}</span>
            <ng-container *ngIf="item.isMine">
              <ng-container *ngTemplateOutlet="deliveryIcon; context: { $implicit: item.message }"></ng-container>
            </ng-container>
          </div>
          <div
            class="chat-thread__bubble small weight-medium radius-3"
            [class.chat-thread__bubble--sending]="item.message.delivery === 'sending'"
            [ngClass]="item.isOwn ? 'bg-primary text-white' : 'bg-accent-lighter text-900'"
          >
            {{ item.message.body }}
          </div>
          <ng-container *ngIf="item.isMine && item.message.delivery === 'failed'">
            <ng-container *ngTemplateOutlet="notSent; context: { $implicit: item.message }"></ng-container>
          </ng-container>
        </div>
      </ng-container>
    </ng-container>
  </ng-container>
</div>

<!-- Offer row, per design: the customer's price goes out as an OFFER message. -->
<div *ngIf="canOffer" class="box noshrink flex wrap align-center gap-3 px-6 py-3 bg-50 brd-t brd-b brd-200">
  <label class="chat-thread__meta weight-medium text-primary-300 text-nowrap m-0" for="chat-offer-amount">{{ 'bidChat.Price' | translate }} (<span riyalCurrency></span>)</label>

  <div class="chat-thread__stepper box grow flex align-center bg-white radius-2">
    <button mat-icon-button type="button" class="box noshrink" [disabled]="!(offerAmount > 0)" [attr.aria-label]="'bidChat.Decrease' | translate" (click)="stepOffer(-1)">
      <app-icon name="outline-minus" size="xxs" class="text-600"></app-icon>
    </button>
    <input
      id="chat-offer-amount"
      type="number"
      inputmode="decimal"
      min="0"
      [step]="offerStep"
      class="chat-thread__shrinkable chat-thread__bare-input box grow h6 m-0 text-center text-900"
      [(ngModel)]="offerAmount"
    />
    <button mat-icon-button type="button" class="box noshrink" [attr.aria-label]="'bidChat.Increase' | translate" (click)="stepOffer(1)">
      <app-icon name="outline-add" size="xxs" class="text-600"></app-icon>
    </button>
  </div>

  <button
    type="button"
    class="chat-thread__offer-button box noshrink flex align-center gap-1 p-2 brd radius-2 h6 m-0 cursor-pointer"
    [ngClass]="canSendOffer ? 'chat-thread__offer-button--ready bg-accent text-white weight-medium' : 'bg-white text-600 weight-normal'"
    [disabled]="!canSendOffer"
    (click)="sendOffer()"
  >
    <app-icon name="outline-send" size="xs"></app-icon>
    <span class="px-1">{{ 'bidChat.SendOffer' | translate }}</span>
  </button>
</div>

<footer class="box noshrink flex align-center gap-3 px-6 py-3" [class.brd-t]="!canOffer" [class.brd-200]="!canOffer">
  <textarea
    *ngIf="showComposer; else cannotSend"
    rows="1"
    [disabled]="!canType"
    [attr.maxlength]="bodyMaxLength"
    class="chat-thread__shrinkable chat-thread__bare-input chat-thread__composer box grow h6 m-0 weight-normal text-900 px-4 brd brd-200 bg-white"
    [placeholder]="'bidChat.TypeMessage' | translate"
    [attr.aria-label]="'bidChat.TypeMessage' | translate"
    [(ngModel)]="messageText"
    (keydown)="onComposerKeydown($event)"
  ></textarea>
  <ng-template #cannotSend>
    <span class="chat-thread__composer box grow flex align-center small text-600 px-4 bg-100">{{ 'bidChat.CannotSendMessages' | translate }}</span>
  </ng-template>

  <button
    mat-icon-button
    type="button"
    class="box noshrink bg-accent text-white"
    [disabled]="!canSendMessage"
    [attr.aria-label]="'bidChat.Send' | translate"
    (click)="sendMessage()"
  >
    <app-icon name="outline-send" size="xs"></app-icon>
  </button>
</footer>

<!-- Own message delivery, like messaging apps: 🕓 sending · ✓ sent · ⚠ failed (no read receipts in this contract). -->
<ng-template #deliveryIcon let-message>
  <app-icon
    *ngIf="message.delivery === 'sending'"
    name="outline-clock"
    direction="ltr"
    role="img"
    size="xxs"
    class="text-500"
    [attr.aria-label]="'bidChat.Sending' | translate"
  ></app-icon>
  <app-icon
    *ngIf="message.delivery === 'sent'"
    name="outline-tick"
    direction="ltr"
    role="img"
    size="xxs"
    class="text-accent"
    [attr.aria-label]="'bidChat.Sent' | translate"
  ></app-icon>
  <app-icon
    *ngIf="message.delivery === 'failed'"
    name="outline-warning"
    direction="ltr"
    role="img"
    size="xxs"
    class="text-warn"
    [attr.aria-label]="'bidChat.NotSent' | translate"
  ></app-icon>
</ng-template>

<ng-template #notSent let-message>
  <span class="chat-thread__meta flex align-center gap-1 text-warn" role="alert">
    {{ 'bidChat.NotSent' | translate }}
    <!-- Retry only while the chat is writable: a closed chat would reject it again. -->
    <ng-container *ngIf="canType">
      ·
      <button type="button" class="chat-thread__link text-warn weight-medium cursor-pointer" (click)="resend(message)">
        {{ 'bidChat.Retry' | translate }}
      </button>
    </ng-container>
  </span>
</ng-template>
